import React from 'react'
import { useTranslation } from 'react-i18next'

import { Avatar } from '@/components/Avatar'
import { CardItem } from '@/components/CardItem'
import { BaseText } from '@/components/base'
import { AdaptedGoalTransferDTO } from '@/domain/transfer'
import { useTheme } from '@/hooks/useTheme'
import { useGetWalletDataByCryptoCode } from '@/hooks/useGetWalletDataByCryptoCode'
import { responsiveAdapter } from '@/utils'

export interface SavingHistoryItemProps extends AdaptedGoalTransferDTO {
  height: number
  onPress?: () => void
  isLastActivities?: boolean
}

export const SavingHistoryItem: React.FC<SavingHistoryItemProps> = ({
  height,
  onPress,
  isLastActivities,
  ...item
}) => {
  const theme = useTheme()
  const { t } = useTranslation()

  const isTransferOut = item.type === 'transfer_out'

  const walletData = useGetWalletDataByCryptoCode({
    cryptoCode: item.amount.currency,
  })

  const adaptedIconBgColor =
    item.displayIconBgColor || walletData?.avatarBGRefColor
  const shouldRenderCurrencyIcon = !item.displayIconName

  return (
    <CardItem.Root
      onPress={onPress}
      style={{
        paddingTop: `${responsiveAdapter(16)}px`,
        paddingBottom: `${responsiveAdapter(16)}px`,
        height: `${height}px`,
      }}
    >
      <div style={{ flexShrink: 0 }}>
        <Avatar
          isCurrency={shouldRenderCurrencyIcon}
          iconName={
            !shouldRenderCurrencyIcon ? item.displayIconName : undefined
          }
          uri={shouldRenderCurrencyIcon ? walletData?.icon : undefined}
          size={48}
          iconSize={item.displayIconSize}
          bgColor={adaptedIconBgColor}
          containerGradientProps={
            Array.isArray(adaptedIconBgColor)
              ? {
                  locations: undefined,
                  start: { x: 0.1, y: 0 },
                  end: { x: 1, y: 1 },
                }
              : undefined
          }
        />
      </div>

      <CardItem.Content
        style={{
          gap: `${responsiveAdapter(2)}px`,
          flex: 1,
          minWidth: 0, // Allows element to shrink below its content size
        }}
      >
        <CardItem.Title
          title={t(item.displayTitle)}
          numberOfLines={1}
          style={{ fontSize: `${responsiveAdapter(16)}px` }}
        />
        <BaseText
          fontSize={16}
          style={{ color: theme.colors.label.description }}
          fontFamily="regular"
        >
          {isLastActivities ? item.formattedDate : item.formattedTime}
        </BaseText>
      </CardItem.Content>

      <CardItem.Footer style={{ flexShrink: 0 }}>
        <BaseText
          fontSize={16}
          fontFamily="semibold"
          numberOfLines={1}
          style={{
            color: !isTransferOut
              ? theme.colors.label.success
              : theme.colors.label.default,
          }}
        >
          {(!isTransferOut ? '+' : '-') + item.formattedCryptoAmount}
        </BaseText>
        <BaseText fontSize={16} fontFamily="medium" color="label.description">
          ≈{item.formattedFiatAmount}
        </BaseText>
      </CardItem.Footer>
    </CardItem.Root>
  )
}
